import React, { useState, useEffect } from 'react';
import { t } from '../utils/translations.js';

const ResourcesHub = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="resources-hub-react">
      <style>{`
        .resources-hub-react {
          background: linear-gradient(135deg, #0F172A 0%, #1E293B 100%);
          position: relative;
          overflow: hidden;
          padding: 0;
        }

        .resources-hub-react::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: 
            radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.05) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.05) 0%, transparent 50%);
          pointer-events: none;
        }

        /* Hero Section */
        .resources-hero {
          position: relative;
          z-index: 1;
          padding: clamp(3rem, 8vw, 6rem) clamp(1.5rem, 5vw, 2rem);
          text-align: center;
          color: white;
        }

        .resources-card {
          max-width: 1200px;
          margin: 0 auto;
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.6s cubic-bezier(0.25, 0.8, 0.25, 1);
        }

        .resources-card.visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* Content Grid Section */
        .resources-content {
          position: relative;
          z-index: 1;
          background: white;
          padding: clamp(3rem, 8vw, 5rem) clamp(1.5rem, 5vw, 2rem);
        }

        .resources-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .resources-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          background: rgba(255,255,255,.08);
          border: 1px solid rgba(255,255,255,.15);
          font-size: 0.78rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          margin-bottom: 1.5rem;
        }

        .resources-icon {
          font-size: 1.2rem;
        }

        .resources-title {
          font-size: clamp(2.2rem, 6vw, 3.2rem);
          font-weight: 800;
          line-height: 1.15;
          letter-spacing: -0.02em;
          margin: 0 0 1.5rem 0;
        }

        .resources-subtitle {
          max-width: 60ch;
          margin: 0 auto 2rem auto;
          color: rgba(255,255,255,.85);
          font-size: 1.05rem;
          line-height: 1.6;
        }

        .resources-actions {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
          justify-content: center;
          margin-bottom: 2rem;
        }

        .resources-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem 2rem;
          border-radius: 12px;
          text-decoration: none;
          font-weight: 700;
          font-size: 1rem;
          transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
          cursor: pointer;
        }

        .resources-btn.primary {
          background: #111827;
          color: white;
          border: 1px solid rgba(255,255,255,.25);
        }

        .resources-btn.primary:hover {
          background: #1f2937;
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(0,0,0,.3);
        }

        .resources-btn.ghost {
          background: transparent;
          color: white;
          border: 1px solid rgba(255,255,255,.35);
        }

        .resources-btn.ghost:hover {
          background: rgba(255,255,255,.1);
          border-color: rgba(255,255,255,.5);
          transform: translateY(-2px);
        }

        .resources-features {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
          margin: 3rem 0 0 0;
        }

        .resources-feature {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          padding: 2rem;
          background: white;
          border-radius: 16px;
          border: 2px solid var(--color-gray-200, #e5e5e5);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 6px rgba(0,0,0,0.05);
        }

        .resources-feature:hover {
          border-color: var(--ghost-accent-color, #6d28d9);
          box-shadow: 0 10px 25px -5px rgba(109, 40, 217, 0.2);
          transform: translateY(-4px);
        }

        .resources-feature-icon {
          font-size: 2.5rem;
          margin-bottom: 0.5rem;
        }

        .resources-feature-title {
          font-weight: 700;
          font-size: 1.25rem;
          margin: 0;
          color: var(--color-gray-900, #171717);
        }

        .resources-feature-desc {
          font-size: 1rem;
          color: var(--color-gray-600, #525252);
          margin: 0;
          line-height: 1.6;
        }

        /* Resource Categories */
        .resource-categories {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 2.5rem;
          margin-top: 3rem;
        }

        .resource-category {
          background: var(--color-gray-50, #fafafa);
          border-radius: 16px;
          padding: 2.5rem;
          border: 2px solid var(--color-gray-200, #e5e5e5);
        }

        .resource-category-title {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0 0 1.5rem 0;
          color: var(--color-gray-900, #171717);
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .resource-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .resource-item {
          padding: 1rem 1.25rem;
          background: white;
          border-radius: 8px;
          border: 1px solid var(--color-gray-200, #e5e5e5);
          transition: all 0.2s ease;
          cursor: not-allowed;
          opacity: 0.7;
        }

        .resource-item:hover {
          border-color: var(--color-gray-300, #d4d4d4);
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }

        .resource-item-title {
          font-weight: 600;
          color: var(--color-gray-900, #171717);
          margin: 0 0 0.25rem 0;
        }

        .resource-item-meta {
          font-size: 0.875rem;
          color: var(--color-gray-500, #737373);
        }

        .coming-soon-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          background: var(--color-primary-100, #f3e8ff);
          color: var(--color-primary-700, #7e22ce);
          border-radius: 999px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-left: 0.5rem;
        }

        .resources-footnote {
          margin-top: 2rem;
          font-size: 0.9rem;
          color: rgba(255,255,255,.8);
          opacity: 0.6;
        }

        /* Light mode cleanup: remove dark block/outline look in hero area */
        [data-theme="light"] .resources-hub-react {
          background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
        }

        [data-theme="light"] .resources-hub-react::before {
          opacity: 0.25;
        }

        [data-theme="light"] .resources-hero {
          color: #0f172a;
        }

        [data-theme="light"] .resources-card {
          background: transparent;
          border: 0;
          box-shadow: none;
          outline: none;
        }

        [data-theme="light"] .resources-badge {
          background: rgba(15, 23, 42, 0.05);
          border-color: rgba(15, 23, 42, 0.14);
          color: #0f172a;
        }

        [data-theme="light"] .resources-title {
          color: #0f172a;
        }

        [data-theme="light"] .resources-subtitle {
          color: #4b5563;
        }

        [data-theme="light"] .resources-btn.primary {
          color: #ffffff !important;
          border: 0;
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.2);
        }

        [data-theme="light"] .resources-btn.primary:hover {
          color: #ffffff !important;
        }

        [data-theme="light"] .resources-btn.ghost {
          color: #111827 !important;
          border-color: rgba(17, 24, 39, 0.28);
        }

        [data-theme="light"] .resources-btn.ghost:hover {
          color: #111827 !important;
          background: rgba(17, 24, 39, 0.06);
          border-color: rgba(17, 24, 39, 0.45);
        }

        /* Dark mode inversion for resources page content surfaces */
        [data-theme="dark"] .resources-hub-react {
          background: linear-gradient(135deg, #020617 0%, #0b1220 100%);
          --resources-footnote-color: #ffffff;
        }

        [data-theme="dark"] .resources-content {
          background: #0f172a;
        }

        [data-theme="dark"] .resources-feature,
        [data-theme="dark"] .resource-category,
        [data-theme="dark"] .resource-item {
          background: #111827;
          border-color: rgba(148, 163, 184, 0.35);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
        }

        [data-theme="dark"] .resources-feature:hover,
        [data-theme="dark"] .resource-item:hover {
          border-color: rgba(129, 140, 248, 0.65);
          box-shadow: 0 12px 28px rgba(30, 64, 175, 0.2);
        }

        [data-theme="dark"] .resources-feature-title,
        [data-theme="dark"] .resource-category-title,
        [data-theme="dark"] .resource-item-title {
          color: #ffffff;
        }

        [data-theme="dark"] .resources-feature-desc,
        [data-theme="dark"] .resource-item-meta {
          color: #ffffff;
        }

        [data-theme="dark"] .coming-soon-badge {
          background: rgba(129, 140, 248, 0.2);
          color: #ffffff;
          border: 1px solid rgba(129, 140, 248, 0.45);
        }

        [data-theme="dark"] .resources-title,
        [data-theme="dark"] .resources-subtitle,
        [data-theme="dark"] .resources-badge,
        [data-theme="dark"] .resources-footnote,
        [data-theme="dark"] .resources-footnote p,
        [data-theme="dark"] .resources-feature,
        [data-theme="dark"] .resource-category,
        [data-theme="dark"] .resource-item,
        [data-theme="dark"] .resource-item * {
          color: #ffffff;
          -webkit-text-fill-color: #ffffff;
        }

        @media (max-width: 720px) {
          .resources-hero {
            padding: 2rem 1.5rem;
          }

          .resources-subtitle {
            font-size: 1rem;
          }

          .resources-actions {
            flex-direction: column;
          }

          .resources-btn {
            width: 100%;
          }

          .resources-features,
          .resource-categories {
            grid-template-columns: 1fr;
          }

          .resource-category {
            padding: 1.5rem;
          }
        }
      `}</style>

      {/* Hero Section */}
      <div className="resources-hero">
        <div className={`resources-card ${isVisible ? 'visible' : ''}`}>
          <div className="resources-badge">
            <span className="resources-icon">🎭</span>
            {t('resources.badge', 'Theatre Resources')}
          </div>
          
          <h1 className="resources-title">{t("resources.title", "Resources & Materials")}</h1>
          
          <p className="resources-subtitle">
            {t('resources.subtitle', 'Explore our collection of performance materials, scripts, creative tools, and theatrical archives. Access curated resources for artists, educators, and theatre enthusiasts.')}
          </p>

          <div className="resources-actions">
            <a href="#/portal/signup" className="resources-btn primary">
              ✨ {t('resources.cta.primary', 'Get Early Access')}
            </a>
            <a href="/" className="resources-btn ghost">
              ← {t('resources.cta.back', 'Back to Home')}
            </a>
          </div>
        </div>
      </div>

      {/* Main Content Section */}
      <div className="resources-content">
        <div className="resources-container">
          <div className="resources-features">
            <div className="resources-feature">
              <span className="resources-feature-icon">📜</span>
              <h3 className="resources-feature-title">{t('resources.features.scripts.title', 'Scripts & Texts')}</h3>
              <p className="resources-feature-desc">{t('resources.features.scripts.desc', 'Original plays, adaptations, and performance texts from our repertoire')}</p>
            </div>
            <div className="resources-feature">
              <span className="resources-feature-icon">🎨</span>
              <h3 className="resources-feature-title">{t('resources.features.assets.title', 'Creative Assets')}</h3>
              <p className="resources-feature-desc">{t('resources.features.assets.desc', 'Posters, photography, set designs, and visual materials')}</p>
            </div>
            <div className="resources-feature">
              <span className="resources-feature-icon">📚</span>
              <h3 className="resources-feature-title">{t('resources.features.archive.title', 'Archive')}</h3>
              <p className="resources-feature-desc">{t('resources.features.archive.desc', 'Historical documentation and performance records since 2002')}</p>
            </div>
          </div>

          {/* Resource Categories */}
          <div className="resource-categories">
            <div className="resource-category">
              <h2 className="resource-category-title">
                📖 {t('resources.categories.published.title', 'Published Works')}
              </h2>
              <ul className="resource-list">
                <li className="resource-item">
                  <div className="resource-item-title">
                    {t('resources.categories.published.scripts', 'Performance Scripts Collection')}
                    <span className="coming-soon-badge">{t('resources.badge.coming', 'Coming Soon')}</span>
                  </div>
                  <div className="resource-item-meta">{t('resources.categories.published.scripts.meta', '10+ original texts and adaptations')}</div>
                </li>
                <li className="resource-item">
                  <div className="resource-item-title">
                    {t('resources.categories.published.analysis', 'Dramaturgical Analysis')}
                    <span className="coming-soon-badge">{t('resources.badge.coming', 'Coming Soon')}</span>
                  </div>
                  <div className="resource-item-meta">{t('resources.categories.published.analysis.meta', 'Behind-the-scenes creative process')}</div>
                </li>
                <li className="resource-item">
                  <div className="resource-item-title">
                    {t('resources.categories.published.records', 'Documentary Records')}
                    <span className="coming-soon-badge">{t('resources.badge.coming', 'Coming Soon')}</span>
                  </div>
                  <div className="resource-item-meta">{t('resources.categories.published.records.meta', 'Production documentation')}</div>
                </li>
              </ul>
            </div>

            <div className="resource-category">
              <h2 className="resource-category-title">
                🎬 {t('resources.categories.media.title', 'Media Library')}
              </h2>
              <ul className="resource-list">
                <li className="resource-item">
                  <div className="resource-item-title">
                    {t('resources.categories.media.videos', 'Performance Videos')}
                    <span className="coming-soon-badge">{t('resources.badge.coming', 'Coming Soon')}</span>
                  </div>
                  <div className="resource-item-meta">{t('resources.categories.media.videos.meta', 'Full recordings and highlights')}</div>
                </li>
                <li className="resource-item">
                  <div className="resource-item-title">
                    {t('resources.categories.media.bts', 'Behind the Scenes')}
                    <span className="coming-soon-badge">{t('resources.badge.coming', 'Coming Soon')}</span>
                  </div>
                  <div className="resource-item-meta">{t('resources.categories.media.bts.meta', 'Rehearsals and creative process')}</div>
                </li>
                <li className="resource-item">
                  <div className="resource-item-title">
                    {t('resources.categories.media.interviews', 'Interviews & Talks')}
                    <span className="coming-soon-badge">{t('resources.badge.coming', 'Coming Soon')}</span>
                  </div>
                  <div className="resource-item-meta">{t('resources.categories.media.interviews.meta', 'Artists and creators discussions')}</div>
                </li>
              </ul>
            </div>

            <div className="resource-category">
              <h2 className="resource-category-title">
                🎓 {t('resources.categories.educational.title', 'Educational')}
              </h2>
              <ul className="resource-list">
                <li className="resource-item">
                  <div className="resource-item-title">
                    {t('resources.categories.educational.training', 'Training Materials')}
                    <span className="coming-soon-badge">{t('resources.badge.coming', 'Coming Soon')}</span>
                  </div>
                  <div className="resource-item-meta">{t('resources.categories.educational.training.meta', 'Workshop guides and exercises')}</div>
                </li>
                <li className="resource-item">
                  <div className="resource-item-title">
                    {t('resources.categories.educational.masterclass', 'Masterclass Series')}
                    <span className="coming-soon-badge">{t('resources.badge.coming', 'Coming Soon')}</span>
                  </div>
                  <div className="resource-item-meta">{t('resources.categories.educational.masterclass.meta', 'Techniques and methodologies')}</div>
                </li>
                <li className="resource-item">
                  <div className="resource-item-title">
                    {t('resources.categories.educational.guides', 'Study Guides')}
                    <span className="coming-soon-badge">{t('resources.badge.coming', 'Coming Soon')}</span>
                  </div>
                  <div className="resource-item-meta">{t('resources.categories.educational.guides.meta', 'For educators and students')}</div>
                </li>
              </ul>
            </div>
          </div>

          <div className="resources-footnote">
            <p style={{ textAlign: 'center', color: 'var(--resources-footnote-color, var(--color-gray-600, #525252))', marginTop: '4rem' }}>
              {t('resources.footnote', 'Subscribe to be notified when our resource library launches')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourcesHub;
